import { baseApi, isRetryableError, resourceListTag, resourceScopeTag, resourceTag } from '@krp/client-runtime'

import { AQSI_WIRE, commandHeader, envelope } from '../../../shared/api/envelope'
import { boolOf, idOf, objectOf } from '../../../shared/api/wire'
import { AQSI_TAG_TYPES, AQSI_TAGS } from '../../../shared/api/tags'
import type { CommandIntent } from '../../../shared/model/ids'
import type {
  AqsiAttentionSale,
  AqsiHeldCarts,
  AqsiSale,
  AqsiSaleCheck,
  AqsiSaleFiscalSnapshot,
  AqsiSaleHoldResult,
  AqsiSaleOperation,
  AqsiSeatActivity,
  PaymentKind,
} from '../model/types'
import type { AqsiSaleState, AqsiSendableAction } from '../model/aqsiStateTypes'
import { AQSI_STATE_KIND, decodeAqsiSaleState } from './aqsiStateContracts'
import {
  decodeActiveSale,
  decodeAttentionSales,
  decodeParkedCount,
  decodeParkedSales,
  decodeFiscalSnapshot,
  decodeHeldCarts,
  decodeHoldResult,
  decodeSaleCheck,
  decodeSale,
  decodeSaleOperation,
} from './contracts'
import { AQSI_SALE_URLS } from './urls'

/**
 * Касса работы: `registerKey` — ключ кассы из ответа службы (ключ кэша и намерений), `deviceId` — касса кабинета,
 * которой адресуются запросы (RP-C9: рабочих мест нет).
 */
export interface SaleSeatArgs {
  resourceId: string
  registerKey: string
  deviceId: number
}

export interface SaleArgs {
  resourceId: string
  saleId: string
}

export interface SaleOperationArgs {
  resourceId: string
  operationId: string
}

/**
 * Позиция запроса: товар кабинета ЭТОГО rid и количество. Цену кабинета браузер
 * не присылает (её берёт служба); `manualPriceKopecks` — только цена кассира,
 * и служба примет её лишь с правом `sales.price` (RP-FB8).
 */
export interface SaleLineRequest {
  productResourceKey: string
  quantity: number
  barcode?: string
  manualPriceKopecks?: number
}

interface CommandArgs extends SaleSeatArgs {
  intent: CommandIntent
  expectedVersion: number
}

export interface SaleCreateArgs extends CommandArgs {
  paymentKind: PaymentKind
  lines: readonly SaleLineRequest[]
  /** UI-RMK: телефон или почта покупателя для чека; `null` — не отправлять. */
  customerContact?: string | null
}

export interface SaleUpdateArgs extends CommandArgs {
  saleId: string
  lines: readonly SaleLineRequest[]
  /**
   * UI-RMK: контакт покупателя. Служба заменяет его вместе с корзиной, поэтому
   * PUT несёт тот, что видит человек (`null` — без контакта).
   */
  customerContact?: string | null
  /** UI-RMK: сменить способ оплаты вместе с корзиной (Resource.Aqsi 0.22.0); не задано — как был. */
  paymentKind?: PaymentKind
}

/** UI-RMK: отложить продажу места — она отменяется, позиции переезжают в отложенный чек. */
export interface SaleHoldArgs extends CommandArgs {
  saleId: string
}

/** UI-RMK: открыть отложенный чек — новая продажа на кассе; `expectedVersion` — версия МОЕЙ сессии. */
export interface HeldCartRestoreArgs extends CommandArgs {
  heldCartId: string
  paymentKind: PaymentKind | null
}

/** UI-RMK: удалить отложенный чек; `expectedVersion` — его версия. */
export interface HeldCartDropArgs extends CommandArgs {
  heldCartId: string
}

export interface SaleCommandArgs extends CommandArgs {
  saleId: string
}

/** «Привязать уже пробитый чек»: причина обязательна; номер чека кабинета — когда подходит несколько. */
export interface SaleIssuedOutsideArgs extends SaleCommandArgs {
  reason: string
  receiptId: string | null
}

/** «Чека нет — пробить заново» (RP-FB35 K3): что проверено и почему чека нет — обе записываются навсегда. */
export interface SaleReceiptRetryArgs extends SaleCommandArgs {
  reason?: string
  checked?: string
}

/**
 * «Пробить чек» из сверки по галочкам (К-128): что подтвердил человек — записывается в журнал продажи. Из панели «Проверка в AQSI»
 * (К-129) — с `action`: служба выполнит его, только если оно всё ещё основное, иначе 409 `state-changed` со свежим состоянием.
 */
export interface SaleReceiptRecoverArgs extends SaleCommandArgs {
  checked?: string
  action?: AqsiSendableAction
}

/** Решение по слипу, найденному после отмены оплаты: `paid` — «Оплата прошла — пробить чек», иначе «Оплаты не было». */
export interface SaleLatePaymentArgs extends SaleCommandArgs {
  paid: boolean
}

/** «Отложить продажу» (RP-FB35 K9): причина необязательна. */
export interface SaleParkArgs extends SaleCommandArgs {
  reason: string | null
}

/** Список отложенных продаж ресурса; `deviceId` сужает до одной кассы. */
export interface ParkedArgs {
  resourceId: string
  deviceId?: number
}

export interface SaleReconcileArgs extends SaleOperationArgs {
  intent: CommandIntent
  /** Версия записи операции. */
  expectedVersion: number
}

export interface OperationResolveArgs extends SaleReconcileArgs {
  outcome: 'Succeeded' | 'Failed'
  reason: string
  checked: string
  resultId: string | null
}

/** Что перечитать, когда продажа места могла измениться, а ответа нет или он — отказ. */
const seatTags = (resourceId: string, registerKey: string) => [
  resourceTag(AQSI_TAGS.saleActive, resourceId, registerKey),
]

/** Список «Требуют внимания» ресурса (число в меню и вкладка). */
const attentionTag = (resourceId: string) => resourceTag(AQSI_TAGS.saleAttention, resourceId, 'ALL')

/** Продажа и отложенные чеки места: «Отложить» и «Открыть» меняют и то, и другое. */
const heldTags = (resourceId: string, registerKey: string) => [
  resourceTag(AQSI_TAGS.saleActive, resourceId, registerKey),
  resourceTag(AQSI_TAGS.heldCarts, resourceId, registerKey),
]

/**
 * Ручки продажи C3b в ОБЩЕМ `baseApi`: имена `aqsi*`, rid в аргументах и тегах,
 * `resourceScopeTag(rid)` у каждого чтения. Команды transport не повторяет
 * (ADR-11): без ответа экран ЧИТАЕТ продажу и операцию, а не шлёт POST ещё раз. Исключение — команды с ключом идемпотентности и
 * версией (`idempotent: true`): их повторяет очередь тем же ключом и телом, служба узнаёт повтор и отдаёт прежний результат.
 */
export const saleApi = baseApi.enhanceEndpoints({ addTagTypes: AQSI_TAG_TYPES }).injectEndpoints({
  endpoints: (build) => ({
    /**
     * Проверка корзины до оплаты под правом кассира (С2): чтение, кассе и кабинету ничего не шлёт. Версия
     * продажи — часть ключа: правка корзины даёт новый запрос, прежняя проверка устаревшей не показывается.
     */
    aqsiSaleCheck: build.query<AqsiSaleCheck, { resourceId: string; saleId: string; saleVersion: number }>({
      query: ({ resourceId, saleId }) => ({
        url: AQSI_SALE_URLS.check(resourceId, saleId),
        decode: envelope('aqsi.sale.check', resourceId, decodeSaleCheck),
      }),
      providesTags: (_result, _error, { resourceId, saleId }) => [
        resourceTag(AQSI_TAGS.saleCheck, resourceId, saleId),
        resourceListTag(AQSI_TAGS.saleCheck, resourceId),
        resourceScopeTag(resourceId),
      ],
    }),
    /** Незавершённая продажа кассы места — так окно восстанавливает работу после F5 и обрыва сети. */
    aqsiActiveSale: build.query<AqsiSeatActivity, SaleSeatArgs>({
      query: ({ resourceId, deviceId }) => ({
        url: AQSI_SALE_URLS.active(resourceId),
        // Расписание повторов ведёт РМК: одна попытка — один HTTP-запрос.
        noRetry: true,
        params: { deviceId },
        decode: envelope('aqsi.sale', resourceId, decodeActiveSale),
      }),
      providesTags: (_result, _error, { resourceId, registerKey }) => [
        resourceTag(AQSI_TAGS.saleActive, resourceId, registerKey),
        resourceListTag(AQSI_TAGS.saleActive, resourceId),
        resourceScopeTag(resourceId),
      ],
    }),
    aqsiSale: build.query<AqsiSale, SaleArgs>({
      query: ({ resourceId, saleId }) => ({
        url: AQSI_SALE_URLS.sale(resourceId, saleId),
        decode: envelope('aqsi.sale', resourceId, (raw) => decodeSale(raw)),
      }),
      providesTags: (_result, _error, { resourceId, saleId }) => [
        resourceTag(AQSI_TAGS.sale, resourceId, saleId),
        resourceScopeTag(resourceId),
      ],
    }),
    aqsiSaleFiscalProfile: build.query<AqsiSaleFiscalSnapshot, SaleArgs>({
      query: ({ resourceId, saleId }) => ({
        url: AQSI_SALE_URLS.fiscalProfile(resourceId, saleId),
        decode: envelope('aqsi.sale.fiscal-profile', resourceId, decodeFiscalSnapshot),
      }),
      providesTags: (_result, _error, { resourceId, saleId }) => [
        resourceTag(AQSI_TAGS.saleFiscal, resourceId, saleId),
        resourceScopeTag(resourceId),
      ],
    }),
    /**
     * «Проверка в AQSI» (RP-CASH-RECOVERY §4.1): служба читает AQSI только GET-запросами и отдаёт строки «терминал / чек / касса» с временем
     * чтения и одно основное действие. Повтор чтения безопасен; снимок служба держит 5 с на продажу.
     */
    aqsiSaleState: build.query<AqsiSaleState, SaleArgs>({
      query: ({ resourceId, saleId }) => ({
        url: AQSI_SALE_URLS.aqsiState(resourceId, saleId),
        decode: envelope(AQSI_STATE_KIND, resourceId, decodeAqsiSaleState),
        // Служба читает кабинет AQSI несколькими запросами по очереди кассы.
        timeoutMs: 60_000,
      }),
      providesTags: (_result, _error, { resourceId, saleId }) => [
        resourceTag(AQSI_TAGS.saleAqsiState, resourceId, saleId.toLowerCase()),
        resourceScopeTag(resourceId),
      ],
    }),
    /** Статус операции продажи из журнала службы — без обращения к кабинету. */
    aqsiSaleOperation: build.query<AqsiSaleOperation, SaleOperationArgs>({
      query: ({ resourceId, operationId }) => ({
        url: AQSI_SALE_URLS.operation(resourceId, operationId),
        decode: envelope('aqsi.operation.status', resourceId, decodeSaleOperation),
      }),
      providesTags: (_result, _error, { resourceId, operationId }) => [
        resourceTag(AQSI_TAGS.operation, resourceId, operationId),
        resourceScopeTag(resourceId),
      ],
    }),

    /** Создать продажу первой позицией. `expectedVersion` — версия МОЕЙ сессии за кассой. */
    aqsiSaleCreate: build.mutation<AqsiSale, SaleCreateArgs>({
      query: ({ resourceId, deviceId, intent, expectedVersion, paymentKind, lines, customerContact }) => ({
        url: AQSI_SALE_URLS.sales(resourceId),
        method: 'POST',
        idempotencyKey: intent.idempotencyKey,
        // Очередь корзины повторяет создание тем же ключом и телом, пока не получит ответ (RP-SALE-FAST): служба вернёт ту же продажу.
        idempotent: true,
        body: {
          ...commandHeader('aqsi.sale.create', resourceId, intent, expectedVersion),
          deviceId,
          paymentKind,
          lines,
          ...(customerContact != null ? { customerContact } : {}),
        },
        decode: envelope('aqsi.sale', resourceId, (raw) => decodeSale(raw)),
      }),
      // Повторяемый сбой (нет ответа, 5xx) продажу не перечитывает: тот же запрос уходит ещё раз (очередь корзины); перечитать — отказ по существу.
      invalidatesTags: (_result, error, { resourceId, registerKey }) =>
        error !== undefined && !isRetryableError(error) ? seatTags(resourceId, registerKey) : [],
    }),
    /** Заменить корзину ЦЕЛИКОМ с версией продажи: чужая параллельная правка — 409, а не тихая перезапись. */
    aqsiSaleUpdateLines: build.mutation<AqsiSale, SaleUpdateArgs>({
      query: ({ resourceId, deviceId, saleId, intent, expectedVersion, lines, customerContact, paymentKind }) => ({
        url: AQSI_SALE_URLS.lines(resourceId, saleId),
        method: 'PUT',
        idempotencyKey: intent.idempotencyKey,
        // Повтор тем же ключом, телом и версией возвращает прежний результат, а не вторую правку (RP-SALE-FAST).
        idempotent: true,
        body: {
          ...commandHeader('aqsi.sale.update', resourceId, intent, expectedVersion),
          saleId,
          deviceId,
          lines,
          ...(customerContact != null ? { customerContact } : {}),
          ...(paymentKind !== undefined ? { paymentKind } : {}),
        },
        decode: envelope('aqsi.sale', resourceId, (raw) => decodeSale(raw)),
      }),
      invalidatesTags: (_result, error, { resourceId, registerKey }) =>
        error !== undefined && !isRetryableError(error) ? seatTags(resourceId, registerKey) : [],
    }),
    /** Оплатить: карта — одно списание на терминале, наличные — сразу чек. Один POST на нажатие. */
    aqsiSalePay: build.mutation<AqsiSale, SaleCommandArgs>({
      query: ({ resourceId, deviceId, saleId, intent, expectedVersion }) => ({
        url: AQSI_SALE_URLS.pay(resourceId, saleId),
        method: 'POST',
        idempotencyKey: intent.idempotencyKey,
        // Повтор тем же ключом, телом и версией возвращает прежний результат, а не второе списание (RP-SALE-FAST, шаг 6): повторяет окно оплаты.
        idempotent: true,
        body: { ...commandHeader('aqsi.sale.pay', resourceId, intent, expectedVersion), saleId, deviceId },
        decode: envelope('aqsi.sale', resourceId, (raw) => decodeSale(raw)),
        // Служба читает смену у кабинета и пишет журнал ДО POST к терминалу.
        timeoutMs: 90_000,
      }),
      // Повторяемый сбой продажу не перечитывает: тот же запрос уходит ещё раз; перечитать — отказ по существу или остановленный повтор.
      invalidatesTags: (_result, error, { resourceId, registerKey }) =>
        error !== undefined && !isRetryableError(error) ? seatTags(resourceId, registerKey) : [],
    }),
    aqsiSaleFiscalize: build.mutation<AqsiSale, SaleCommandArgs>({
      query: ({ resourceId, deviceId, saleId, intent, expectedVersion }) => ({
        url: AQSI_SALE_URLS.fiscalize(resourceId, saleId),
        method: 'POST',
        idempotencyKey: intent.idempotencyKey,
        idempotent: true,
        body: { ...commandHeader('aqsi.sale.fiscalize', resourceId, intent, expectedVersion), saleId, deviceId },
        decode: envelope('aqsi.sale', resourceId, (raw) => decodeSale(raw)),
        timeoutMs: 90_000,
      }),
      // «Требуют внимания» меняется и при успехе: причина снята, отложена или стала другой — ответ команды её не несёт.
      invalidatesTags: (_result, error, { resourceId, registerKey }) =>
        error === undefined
          ? [attentionTag(resourceId)]
          : isRetryableError(error)
            ? []
            : [...seatTags(resourceId, registerKey), attentionTag(resourceId)],
    }),
    /**
     * «Чека нет — пробить заново»: новая попытка чека по слову человека, только после срока жизни прежней (409 `operation-in-flight`).
     * Ретрая нет (ADR-11): ответ потерян — исход читается продажей, ключ попытки назначает служба.
     */
    aqsiSaleReceiptRetry: build.mutation<AqsiSale, SaleReceiptRetryArgs>({
      query: ({ resourceId, deviceId, saleId, intent, expectedVersion, reason, checked }) => ({
        url: AQSI_SALE_URLS.receiptRetry(resourceId, saleId),
        method: 'POST',
        idempotencyKey: intent.idempotencyKey,
        body: { ...commandHeader('aqsi.sale.receipt-retry', resourceId, intent, expectedVersion), saleId, deviceId, reason, checked },
        decode: envelope('aqsi.sale', resourceId, (raw) => decodeSale(raw)),
        timeoutMs: 90_000,
      }),
      // «Требуют внимания» меняется и при успехе: причина снята, отложена или стала другой — ответ команды её не несёт.
      invalidatesTags: (_result, error, { resourceId, registerKey }) =>
        error !== undefined ? [...seatTags(resourceId, registerKey), attentionTag(resourceId)] : [attentionTag(resourceId)],
    }),
    /**
     * «Пробить чек» из ручной сверки по галочкам (К-128, RP-CASH-RECOVERY §4.2): деньги списаны, чека в ФН нет. Служба заново читает
     * кассу и выполняет действие, только если оно и сейчас основное (иначе 409 со словами AQSI: `payment-required`,
     * `receipt-not-fiscalized`, `receipt-ambiguous`, `receipt-printing`, `duplicate-suspected`, `version-mismatch`). Ретрая нет (ADR-11):
     * ответ потерян — исход читается продажей. Из панели «Проверка в AQSI» (К-129) — с кодом основного действия (`action`); отказ
     * `state-changed` несёт свежее состояние.
     */
    aqsiSaleReceiptRecover: build.mutation<AqsiSale, SaleReceiptRecoverArgs>({
      query: ({ resourceId, deviceId, saleId, intent, expectedVersion, checked, action }) => ({
        url: AQSI_SALE_URLS.receiptRecover(resourceId, saleId),
        method: 'POST',
        idempotencyKey: intent.idempotencyKey,
        body: {
          ...commandHeader('aqsi.sale.receipt-recover', resourceId, intent, expectedVersion),
          saleId,
          deviceId,
          checked,
          ...(action !== undefined ? { action } : {}),
        },
        decode: envelope('aqsi.sale', resourceId, (raw) => decodeSale(raw)),
        timeoutMs: 90_000,
      }),
      // «Проверка в AQSI» меняется и при успехе, и при отказе — перечитывается, если панель открыта.
      invalidatesTags: (_result, error, { resourceId, registerKey, saleId }) => [
        resourceTag(AQSI_TAGS.saleAqsiState, resourceId, saleId.toLowerCase()),
        ...(error !== undefined ? [...seatTags(resourceId, registerKey), attentionTag(resourceId)] : [attentionTag(resourceId)]),
      ],
    }),
    /**
     * «Отменить» оплату картой, пока терминал ждёт карту: служба просит кабинет снять операцию и читает итог (отменена — деньги не взяты;
     * покупатель успел заплатить — оплата подтверждена). Повтор безопасен: это просьба, а не списание.
     */
    aqsiSaleCancelPayment: build.mutation<AqsiSale, SaleCommandArgs>({
      query: ({ resourceId, deviceId, saleId, intent, expectedVersion }) => ({
        url: AQSI_SALE_URLS.cancelPayment(resourceId, saleId),
        method: 'POST',
        idempotencyKey: intent.idempotencyKey,
        idempotent: true,
        body: { ...commandHeader('aqsi.sale.cancel-payment', resourceId, intent, expectedVersion), saleId, deviceId },
        decode: envelope('aqsi.sale', resourceId, (raw) => decodeSale(raw)),
        timeoutMs: 60_000,
      }),
      // Отмена без ответа ждёт связи в очереди и уходит тем же ключом: пока она ждёт, продажу перечитывать не нужно.
      invalidatesTags: (_result, error, { resourceId, registerKey }) =>
        error !== undefined && isRetryableError(error) ? [] : seatTags(resourceId, registerKey),
    }),
    /**
     * Слип покупки нашёлся уже после отмены оплаты (внимание `late-payment-slip`): человек решает, была ли оплата. `paid: true` - служба
     * считает продажу оплаченной и сама отправляет чек; `false` - оплаты не было. Отказ 409 (`late-slip-*`) несёт русский текст - дословно.
     */
    aqsiSaleLatePayment: build.mutation<AqsiSale, SaleLatePaymentArgs>({
      query: ({ resourceId, deviceId, saleId, intent, expectedVersion, paid }) => ({
        url: AQSI_SALE_URLS.latePayment(resourceId, saleId),
        method: 'POST',
        idempotencyKey: intent.idempotencyKey,
        body: { ...commandHeader('aqsi.sale.late-payment', resourceId, intent, expectedVersion), saleId, deviceId, paid },
        decode: envelope('aqsi.sale', resourceId, (raw) => decodeSale(raw)),
        timeoutMs: 60_000,
      }),
      // Продажа, список отложенных и «Требуют внимания» меняются и при успехе, и при отказе: внимание снято или осталось.
      invalidatesTags: (_result, _error, { resourceId, registerKey }) => [
        ...seatTags(resourceId, registerKey),
        resourceTag(AQSI_TAGS.saleParked, resourceId, 'LIST'),
        resourceTag(AQSI_TAGS.saleParked, resourceId, 'COUNT'),
        attentionTag(resourceId),
      ],
    }),
    aqsiSaleCancel: build.mutation<AqsiSale, SaleCommandArgs>({
      query: ({ resourceId, deviceId, saleId, intent, expectedVersion }) => ({
        url: AQSI_SALE_URLS.cancel(resourceId, saleId),
        method: 'POST',
        idempotencyKey: intent.idempotencyKey,
        idempotent: true,
        body: { ...commandHeader('aqsi.sale.cancel', resourceId, intent, expectedVersion), saleId, deviceId },
        decode: envelope('aqsi.sale', resourceId, (raw) => decodeSale(raw)),
      }),
      invalidatesTags: (_result, error, { resourceId, registerKey }) =>
        error !== undefined ? seatTags(resourceId, registerKey) : [],
    }),
    /**
     * Привязать уже пробитый чек мимо программы (право `sales.reconcile`): служба
     * ищет его в журнале чеков (касса, сумма, направление, время после оплаты)
     * и закрывает им продажу. POST к кассе служба не шлёт; отказы — 409 с текстом.
     */
    aqsiSaleIssuedOutside: build.mutation<AqsiSale, SaleIssuedOutsideArgs>({
      query: ({ resourceId, deviceId, saleId, intent, expectedVersion, reason, receiptId }) => ({
        url: AQSI_SALE_URLS.issuedOutside(resourceId, saleId),
        method: 'POST',
        idempotencyKey: intent.idempotencyKey,
        body: {
          ...commandHeader('aqsi.sale.issued-outside', resourceId, intent, expectedVersion),
          saleId,
          deviceId,
          reason,
          ...(receiptId !== null ? { receiptId } : {}),
        },
        decode: envelope('aqsi.sale', resourceId, (raw) => decodeSale(raw)),
        timeoutMs: 60_000,
      }),
      // «Требуют внимания» меняется и при успехе: причина снята, отложена или стала другой — ответ команды её не несёт.
      invalidatesTags: (_result, error, { resourceId, registerKey }) =>
        error !== undefined ? [...seatTags(resourceId, registerKey), attentionTag(resourceId)] : [attentionTag(resourceId)],
    }),
    /** RP-FB35 K9: отложенные продажи ресурса (деньги взяты, чек не пробит) — отдельно от отложенных корзин. */
    aqsiParkedSales: build.query<readonly AqsiSale[], ParkedArgs>({
      query: ({ resourceId, deviceId }) => ({
        url: AQSI_SALE_URLS.parked(resourceId),
        params: { limit: 100, ...(deviceId !== undefined ? { deviceId } : {}) },
        decode: envelope('aqsi.sale.parked-list', resourceId, decodeParkedSales),
      }),
      providesTags: (_result, _error, { resourceId }) => [
        resourceTag(AQSI_TAGS.saleParked, resourceId, 'LIST'),
        resourceScopeTag(resourceId),
      ],
    }),
    /** Число отложенных продаж с деньгами — для заметки в окне и числа в меню. */
    aqsiParkedCount: build.query<number, ParkedArgs>({
      query: ({ resourceId, deviceId }) => ({
        url: AQSI_SALE_URLS.parkedCount(resourceId),
        ...(deviceId !== undefined ? { params: { deviceId } } : {}),
        decode: envelope('aqsi.sale.parked-count', resourceId, decodeParkedCount),
      }),
      providesTags: (_result, _error, { resourceId }) => [
        resourceTag(AQSI_TAGS.saleParked, resourceId, 'COUNT'),
        resourceScopeTag(resourceId),
      ],
    }),
    /**
     * «Отложить продажу»: касса освобождается, долг по чеку остаётся. Ретрая нет (ADR-11); ответ потерян — исход читается продажей и
     * списком отложенных.
     */
    aqsiSalePark: build.mutation<AqsiSale, SaleParkArgs>({
      query: ({ resourceId, deviceId, saleId, intent, expectedVersion, reason }) => ({
        url: AQSI_SALE_URLS.park(resourceId, saleId),
        method: 'POST',
        idempotencyKey: intent.idempotencyKey,
        body: { ...commandHeader('aqsi.sale.park', resourceId, intent, expectedVersion), saleId, deviceId, ...(reason !== null ? { reason } : {}) },
        decode: envelope('aqsi.sale', resourceId, (raw) => decodeSale(raw)),
        timeoutMs: 60_000,
      }),
      invalidatesTags: (_result, _error, { resourceId, registerKey }) => [
        ...seatTags(resourceId, registerKey),
        resourceTag(AQSI_TAGS.saleParked, resourceId, 'LIST'),
        resourceTag(AQSI_TAGS.saleParked, resourceId, 'COUNT'),
        attentionTag(resourceId),
      ],
    }),
    /** «Вернуть на кассу»: отложенная продажа снова продажа кассы; занята — 409 `register-busy`. */
    aqsiSaleUnpark: build.mutation<AqsiSale, SaleCommandArgs>({
      query: ({ resourceId, deviceId, saleId, intent, expectedVersion }) => ({
        url: AQSI_SALE_URLS.unpark(resourceId, saleId),
        method: 'POST',
        idempotencyKey: intent.idempotencyKey,
        body: { ...commandHeader('aqsi.sale.unpark', resourceId, intent, expectedVersion), saleId, deviceId },
        decode: envelope('aqsi.sale', resourceId, (raw) => decodeSale(raw)),
        timeoutMs: 60_000,
      }),
      invalidatesTags: (_result, _error, { resourceId, registerKey }) => [
        ...seatTags(resourceId, registerKey),
        resourceTag(AQSI_TAGS.saleParked, resourceId, 'LIST'),
        resourceTag(AQSI_TAGS.saleParked, resourceId, 'COUNT'),
        attentionTag(resourceId),
      ],
    }),
    /** RP-FB35 K7: продажи ресурса, требующие внимания, — после F5 и на другом компьютере (`duplicate-suspected` и др.). */
    aqsiSalesAttention: build.query<readonly AqsiAttentionSale[], { resourceId: string }>({
      query: ({ resourceId }) => ({
        url: AQSI_SALE_URLS.attention(resourceId),
        params: { limit: 100 },
        decode: envelope('aqsi.sale.attention-list', resourceId, decodeAttentionSales),
      }),
      providesTags: (_result, _error, { resourceId }) => [
        resourceTag(AQSI_TAGS.saleAttention, resourceId, 'ALL'),
        resourceScopeTag(resourceId),
      ],
    }),
    /** UI-RMK: отложенные чеки кассы места — от новых к старым (сервер, не браузер). */
    aqsiHeldCarts: build.query<AqsiHeldCarts, SaleSeatArgs>({
      query: ({ resourceId, deviceId }) => ({
        url: AQSI_SALE_URLS.heldCarts(resourceId),
        params: { deviceId },
        decode: envelope('aqsi.held-carts', resourceId, decodeHeldCarts),
      }),
      providesTags: (_result, _error, { resourceId, registerKey }) => [
        resourceTag(AQSI_TAGS.heldCarts, resourceId, registerKey),
        resourceScopeTag(resourceId),
      ],
    }),
    /** «Отложить»: продажа без начатой оплаты отменяется, её позиции становятся отложенным чеком — одной транзакцией. */
    aqsiSaleHold: build.mutation<AqsiSaleHoldResult, SaleHoldArgs>({
      query: ({ resourceId, deviceId, saleId, intent, expectedVersion }) => ({
        url: AQSI_SALE_URLS.hold(resourceId, saleId),
        method: 'POST',
        idempotencyKey: intent.idempotencyKey,
        body: { ...commandHeader('aqsi.sale.hold', resourceId, intent, expectedVersion), saleId, deviceId },
        decode: envelope('aqsi.sale.hold', resourceId, decodeHoldResult),
      }),
      invalidatesTags: (_result, _error, { resourceId, registerKey }) => heldTags(resourceId, registerKey),
    }),
    /** «Открыть» отложенный: новая продажа с его позициями (цены — сегодняшние), сам чек удаляется той же транзакцией. */
    aqsiHeldCartRestore: build.mutation<AqsiSale, HeldCartRestoreArgs>({
      query: ({ resourceId, deviceId, heldCartId, intent, expectedVersion, paymentKind }) => ({
        url: AQSI_SALE_URLS.heldRestore(resourceId, heldCartId),
        method: 'POST',
        idempotencyKey: intent.idempotencyKey,
        body: {
          ...commandHeader('aqsi.held-cart.restore', resourceId, intent, expectedVersion),
          heldCartId,
          deviceId,
          ...(paymentKind !== null ? { paymentKind } : {}),
        },
        decode: envelope('aqsi.sale', resourceId, (raw) => decodeSale(raw)),
      }),
      invalidatesTags: (_result, _error, { resourceId, registerKey }) => heldTags(resourceId, registerKey),
    }),
    aqsiHeldCartDrop: build.mutation<{ heldCartId: string; dropped: boolean }, HeldCartDropArgs>({
      query: ({ resourceId, deviceId, heldCartId, intent, expectedVersion }) => ({
        url: AQSI_SALE_URLS.heldDrop(resourceId, heldCartId),
        method: 'POST',
        idempotencyKey: intent.idempotencyKey,
        body: { ...commandHeader('aqsi.held-cart.drop', resourceId, intent, expectedVersion), heldCartId, deviceId },
        decode: envelope('aqsi.held-cart.drop', resourceId, (raw) => {
          const data = objectOf(raw, 'удаление отложенного чека')
          return { heldCartId: idOf(data.heldCartId, 'heldCartId'), dropped: boolOf(data.dropped, 'dropped') }
        }),
      }),
      invalidatesTags: (_result, _error, { resourceId, registerKey }) => [resourceTag(AQSI_TAGS.heldCarts, resourceId, registerKey)],
    }),
    /** Сверка: служба только ЧИТАЕТ операцию кабинета по сохранённому id — ни одного POST к кассе. */
    aqsiSaleReconcile: build.mutation<AqsiSaleOperation, SaleReconcileArgs>({
      query: ({ resourceId, operationId, intent, expectedVersion }) => ({
        url: AQSI_SALE_URLS.reconcile(resourceId, operationId),
        method: 'POST',
        idempotencyKey: intent.idempotencyKey,
        body: {
          kind: 'aqsi.operation.reconcile',
          resourceId,
          contractVersion: AQSI_WIRE.contractVersion,
          schemaVersion: AQSI_WIRE.schemaVersion,
          requestId: intent.operationId,
          idempotencyKey: intent.idempotencyKey,
          expectedVersion,
        },
        decode: envelope('aqsi.operation.status', resourceId, decodeSaleOperation),
        timeoutMs: 60_000,
      }),
      invalidatesTags: (_result, error, { resourceId, operationId }) =>
        error !== undefined ? [resourceTag(AQSI_TAGS.operation, resourceId, operationId)] : [],
    }),
    /**
     * Ручная сверка Unknown (решение владельца 07.10.2026): кто — из токена,
     * причина, что проверено, итог. Кабинет служба не вызывает вовсе.
     */
    aqsiOperationResolve: build.mutation<AqsiSaleOperation, OperationResolveArgs>({
      query: ({ resourceId, operationId, intent, expectedVersion, outcome, reason, checked, resultId }) => ({
        url: AQSI_SALE_URLS.resolve(resourceId, operationId),
        method: 'POST',
        idempotencyKey: intent.idempotencyKey,
        body: {
          kind: 'aqsi.operation.resolve',
          resourceId,
          contractVersion: AQSI_WIRE.contractVersion,
          schemaVersion: AQSI_WIRE.schemaVersion,
          requestId: intent.operationId,
          idempotencyKey: intent.idempotencyKey,
          expectedVersion,
          outcome,
          reason,
          checked,
          ...(resultId !== null ? { resultId } : {}),
        },
        decode: envelope('aqsi.operation.status', resourceId, decodeSaleOperation),
      }),
      invalidatesTags: (_result, _error, { resourceId, operationId }) => [
        resourceTag(AQSI_TAGS.operation, resourceId, operationId),
        resourceListTag(AQSI_TAGS.saleActive, resourceId),
        attentionTag(resourceId),
      ],
    }),
  }),
})

export const {
  aqsiActiveSale,
  aqsiSaleCheck,
  aqsiSale,
  aqsiSaleFiscalProfile,
  aqsiSaleOperation,
  aqsiSaleCreate,
  aqsiSaleUpdateLines,
  aqsiSalePay,
  aqsiSaleFiscalize,
  aqsiSaleReceiptRetry,
  aqsiSaleState,
  aqsiSaleReceiptRecover,
  aqsiSaleCancel,
  aqsiSaleCancelPayment,
  aqsiSaleLatePayment,
  aqsiSaleIssuedOutside,
  aqsiSaleReconcile,
  aqsiOperationResolve,
  aqsiHeldCarts,
  aqsiSalesAttention,
  aqsiParkedSales,
  aqsiParkedCount,
  aqsiSalePark,
  aqsiSaleUnpark,
  aqsiSaleHold,
  aqsiHeldCartRestore,
  aqsiHeldCartDrop,
} = saleApi.endpoints
